// Stores
import { useAppDataStore } from './appDataStore';

// Types
import {
  CompletionProgress,
  CompletionProgressFormType,
  FormType,
  MemberDetails,
  PaymentSummary,
  PaymentSummaryCarItem,
  PaymentSummaryEntrantItem,
  PaymentSummaryEventItem,
  PaymentSummaryMealItem,
  PrimaryEntrantDetails,
  PaymentSummaryItem,
} from '@/types/user';
import { AllRegisteredEntrantsResponse, CompletionProgressResponse, PaymentSummaryResponse, PaymentSummaryResponseItem, PrimaryEntrantDetailsResponse } from '@/types/apiResponses';
import { EventEntrantOption } from '@/types/event';
import { CarsAllowed, MealEntrantItem, MealEntree, ParadeEvent, ParadeMeal, PricingItem } from 'parade_types';

// Utils
import { clearAllLocalStorage } from '@/utils/localStorage';
import { defineStore } from 'pinia';
import { formatPrice } from '@/utils/dollaDollaBillzYall';
import { fetchAllRegisteredEntrants, fetchCompletionProgress, fetchPaymentSummary, fetchPrimaryEntrantDetails } from '@/api/apiCalls';
import { getCookie, setCookie, removeCookie } from '@/utils/cookieMonster';

// Local types
type UserDataStoreState = {
  allRegisteredEntrants: AllRegisteredEntrantsResponse;
  authToken: string | null;
  completionProgress: CompletionProgress;
  memberDetails: MemberDetails;
  paymentSummary: PaymentSummary;
  primaryEntrantDetails: PrimaryEntrantDetails | null;
};

export const authTokenName = 'authToken';

export const emptyPaymentSummary: PaymentSummary = {
  [FormType.PRIMARY_ENTRANT]: [],
  [FormType.CO_ENTRANT]: [],
  [FormType.CHILD]: [],
  [FormType.GUEST]: [],
  [FormType.EVENT]: [],
  [FormType.MEAL]: [],
  [FormType.CAR]: [],
};

export const emptyCompletionProgress: CompletionProgress = {
  [CompletionProgressFormType.CHECKOUT]: false,
  [CompletionProgressFormType.COENTRANT]: false,
  [CompletionProgressFormType.GUEST]: false,
  [CompletionProgressFormType.PRIMARYENTRANT]: false,
  [CompletionProgressFormType.CHILD]: false,
};

export const useUserDataStore = defineStore('userDataStore', {
  state: (): UserDataStoreState => ({
    allRegisteredEntrants: [] as AllRegisteredEntrantsResponse,
    authToken: getCookie(authTokenName),
    completionProgress: emptyCompletionProgress,
    memberDetails: {} as MemberDetails,
    paymentSummary: emptyPaymentSummary,
    primaryEntrantDetails: null,
  }),

  getters: {
    /**
     * @name getAllRegisteredEntrants
     * @description Returns the allRegisteredEntrants
     */
    getAllRegisteredEntrants(): UserDataStoreState['allRegisteredEntrants'] {
      return this.allRegisteredEntrants;
    },

    /**
     * @name getPaymentSummary
     * @description Returns the paymentSummary
     */
    getPaymentSummary(): UserDataStoreState['paymentSummary'] {
      return this.paymentSummary;
    },

    /**
     * @name getAuthToken
     * @description Returns the authToken
     */
    getAuthToken(): UserDataStoreState['authToken'] {
      return this.authToken;
    },

    /**
     * @name getMemberDetails
     * @description Returns the memberDetails
     */
    getMemberDetails(): UserDataStoreState['memberDetails'] {
      return this.memberDetails;
    },

    /**
     * @name getMemberNumber
     * @description Returns the PCA member number
     */
    getMemberNumber(): UserDataStoreState['memberDetails']['MEMBERNUMBER'] {
      return this.memberDetails.MEMBERNUMBER;
    },

    /**
     * @name getPrimaryEntrantDetails
     * @description Returns the primaryEntrantDetails
     */
    getPrimaryEntrantDetails(): UserDataStoreState['primaryEntrantDetails'] {
      return this.primaryEntrantDetails;
    },

    /**
     * @name getFormattedBalanceDue
     * @description Returns the balance due for unpaid entrants as a formatted string $XX.XX
     */
    getFormattedBalanceDue(): string {
      return formatPrice(this.getBalanceDue);
    },

    /**
     * @name getBalanceDue
     * @description Computes the balance due for unpaid entrants as number
     */
    getBalanceDue(): number {
      let balanceDue: number = 0;
      for (const formType in this.paymentSummary) {
        for (const entrantItem of this.paymentSummary[formType as keyof PaymentSummary]) {
          if (!entrantItem.paid) {
            balanceDue += entrantItem.price;
          }
        }
      }
      return balanceDue;
    },

    /**
     * @name getTotalPaid
     * @description Computes the total paid to date
     */
    getTotalPaid(): string {
      let totalPaid: number = 0;
      for (const formType in this.paymentSummary) {
        for (const entrantItem of this.paymentSummary[formType as keyof PaymentSummary]) {
          if (entrantItem.paid) {
            totalPaid += entrantItem.price;
          }
        }
      }
      return formatPrice(totalPaid);
    },

    /**
     * @name getCompletionProgress
     * @description Returns the completionProgress
     */
    getCompletionProgress(): UserDataStoreState['completionProgress'] {
      return this.completionProgress;
    },

    /**
     * @name getCheckoutComplete
     * @description Returns true if the checkout form is complete (user has paid)
     */
    getCheckoutComplete(): boolean {
      return this.getPrimaryEntrantDetails !== null && this.getPrimaryEntrantDetails.PHASE1PAID === 1;
    },

    /**
     * @name getPrimaryIsRegistered
     * @description Returns true if the primary entrant is registered
     */
    getPrimaryIsRegistered(): boolean {
      return this.getPrimaryEntrantDetails !== null;
    },

    /**
     * @name getEntrantNumber
     * @description Returns the entrant number if checkout is complete - else returns null
     */
    getEntrantNumber(): string | null {
      return this.getCheckoutComplete && this.getPrimaryEntrantDetails !== null && this.getPrimaryEntrantDetails.ENTRANT_NUMBER !== null ? this.getPrimaryEntrantDetails.ENTRANT_NUMBER : null;
    },

    /**
     * @name getCompletionProgressPercentage
     * @description Returns the percentage of forms completed
     */
    getCompletionProgressPercentage(): number {
      let percentage: number = 0;
      for (const isComplete of Object.values(this.completionProgress)) {
        if (isComplete) {
          percentage += 20;
        }
      }
      return percentage;
    },

    getIsEditingSelectedItem(): (selectedItemId: number, routeId: string | string[]) => boolean {
      return (selectedItemId: number, routeId: string | string[]) => {
        return typeof routeId === 'string' && selectedItemId.toString() === routeId.toString();
      };
    },

    getCarOptions(): (eventId: number, isEditMode: boolean) => { title: string; value: number }[] {
      return (eventId: number, isEditMode: boolean) => {
        const appDataStore = useAppDataStore();
        const event: ParadeEvent = appDataStore.getEventDetailsById(eventId);

        const registeredCars: PaymentSummaryCarItem[] = this.paymentSummary.car.filter((car: PaymentSummaryCarItem) => {
          if (event.ENTER_BY === 'car') {
            // For 'enter by car' events, we omit cars already registered for the event
            const paymentSummaryEventItem: PaymentSummaryEventItem | undefined = this.paymentSummary.event.find(
              (paymentSummaryEvent: PaymentSummaryEventItem) => paymentSummaryEvent.eventId === eventId && paymentSummaryEvent.carId === car.carId
            );
            return isEditMode ? true : paymentSummaryEventItem === undefined;
          } else {
            return car;
          }
        });

        const carOptions: { title: string; value: number }[] = [];

        // Early out if any registered cars are eligible
        if (event.CARS_ENTERED === CarsAllowed.ALL.toLowerCase()) {
          // @ZACK - need to update parade_types so CarAllowed enum is all lowercase
          return registeredCars.map((carOption: PaymentSummaryCarItem, index: number) => {
            if (carOption.isPorsche) {
              return {
                title: `${carOption.year} ${carOption.modelName} (${carOption.color})`,
                value: carOption.carId,
              };
            } else {
              return {
                title: `Non-Porsche Car ${index}`,
                value: carOption.carId,
              };
            }
          });
        }

        if (event.CARS_ENTERED === CarsAllowed.REGISTERED_AND_STREET_LEGAL.toLowerCase()) {
          // @ZACK - need to update parade_types so CarAllowed enum is all lowercase
          registeredCars.forEach((carOption: PaymentSummaryCarItem, index: number) => {
            if (carOption.legal && carOption.isPorsche && carOptions.find((item: { title: string; value: number }) => item.value === carOption.carId) === undefined) {
              if (carOption.isPorsche) {
                carOptions.push({
                  title: `${carOption.year} ${carOption.modelName} (${carOption.color})`,
                  value: carOption.carId,
                });
              } else {
                carOptions.push({
                  title: `Non-Porsche Car ${index}`,
                  value: carOption.carId,
                });
              }
            }
          });
        }

        if (event.CARS_ENTERED === CarsAllowed.ANY_LEGAL.toLowerCase()) {
          // @ZACK - need to update parade_types so CarAllowed enum is all lowercase
          registeredCars.forEach((carOption: PaymentSummaryCarItem, index: number) => {
            if (carOption.legal && carOptions.find((item: { title: string; value: number }) => item.value === carOption.carId) === undefined) {
              if (carOption.isPorsche) {
                carOptions.push({
                  title: `${carOption.year} ${carOption.modelName} (${carOption.color})`,
                  value: carOption.carId,
                });
              } else {
                carOptions.push({
                  title: `Non-Porsche Car ${index}`,
                  value: carOption.carId,
                });
              }
            }
          });
        }

        if (event.CARS_ENTERED === CarsAllowed.REGISTERED.toLowerCase()) {
          // @ZACK - need to update parade_types so CarAllowed enum is all lowercase
          registeredCars.forEach((carOption: PaymentSummaryCarItem, index: number) => {
            if (carOption.isPorsche && carOptions.find((item: { title: string; value: number }) => item.value === carOption.carId) === undefined) {
              if (carOption.isPorsche) {
                carOptions.push({
                  title: `${carOption.year} ${carOption.modelName} (${carOption.color})`,
                  value: carOption.carId,
                });
              } else {
                carOptions.push({
                  title: `Non-Porsche Car ${index}`,
                  value: carOption.carId,
                });
              }
            }
          });
        }

        return carOptions;
      };
    },
  },

  actions: {
    login(token: string): void {
      setCookie(authTokenName, token);
      this.authToken = token;
    },

    logout(): void {
      removeCookie(authTokenName);
      this.authToken = null;
      this.memberDetails = {} as MemberDetails;
      this.primaryEntrantDetails = null;
      this.completionProgress = emptyCompletionProgress;
      this.paymentSummary = emptyPaymentSummary;
      clearAllLocalStorage();
    },

    setMemberDetailsData(data: UserDataStoreState['memberDetails']): void {
      this.memberDetails = data;
    },

    setPrimaryEntrantDetailsData(data: UserDataStoreState['primaryEntrantDetails']): void {
      this.primaryEntrantDetails = data;
    },

    setPaymentSummaryData(data: PaymentSummaryResponse): void {
      const appDataStore = useAppDataStore();
      const paymentSummary: PaymentSummary = {
        [FormType.PRIMARY_ENTRANT]: [],
        [FormType.CO_ENTRANT]: [],
        [FormType.CHILD]: [],
        [FormType.GUEST]: [],
        [FormType.EVENT]: [],
        [FormType.MEAL]: [],
        [FormType.CAR]: [],
      };
      for (const formType in data) {
        if (formType === 'PRIMARY_ENTRANT' && Object.keys(data[formType]).length > 0) {
          const primaryEntrant: AllRegisteredEntrantsResponse[number] | undefined = this.allRegisteredEntrants.find((entrant: AllRegisteredEntrantsResponse[number]) => entrant.ID === data[formType].ID)
          paymentSummary[FormType.PRIMARY_ENTRANT] = [
            {
              id: data[formType].ID,
              firstName: data[formType].FIRSTNAME,
              lastName: data[formType].LASTNAME,
              paid: Boolean(data[formType].PAID),
              price: primaryEntrant !== undefined ? primaryEntrant.COST : 0,
              formType: FormType.PRIMARY_ENTRANT,
            },
          ];
        }
        if (formType === 'CO_ENTRANT' && Object.keys(data[formType]).length > 0) {
          const coEntrant: AllRegisteredEntrantsResponse[number] | undefined = this.allRegisteredEntrants.find((entrant: AllRegisteredEntrantsResponse[number]) => entrant.ID === data[formType].ID)
          paymentSummary[FormType.CO_ENTRANT] = [
            {
              id: data[formType].ID,
              firstName: data[formType].FIRSTNAME,
              lastName: data[formType].LASTNAME,
              paid: Boolean(data[formType].PAID),
              price: coEntrant !== undefined ? coEntrant.COST : 0,
              formType: FormType.CO_ENTRANT,
            },
          ];
        }
        if (formType === 'CHILD') {
          paymentSummary[FormType.CHILD] = (data[formType as keyof PaymentSummaryResponse] as PaymentSummaryResponse['CHILD']).map((item: PaymentSummaryResponseItem) => {
            const childEntrant: AllRegisteredEntrantsResponse[number] | undefined = this.allRegisteredEntrants.find((entrant: AllRegisteredEntrantsResponse[number]) => entrant.ID === item.ID)
            return ({
              id: item.ID,
              firstName: item.FIRSTNAME,
              lastName: item.LASTNAME,
              paid: Boolean(item.PAID),
              price: childEntrant !== undefined ? childEntrant.COST : 0,
              formType: FormType.CHILD,
            })
          });
        }
        if (formType === 'GUEST') {
          paymentSummary[FormType.GUEST] = (data[formType as keyof PaymentSummaryResponse] as PaymentSummaryResponse['GUEST']).map((item: PaymentSummaryResponseItem) => {
            const guestEntrant: AllRegisteredEntrantsResponse[number] | undefined = this.allRegisteredEntrants.find((entrant: AllRegisteredEntrantsResponse[number]) => entrant.ID === item.ID)
            return ({
              id: item.ID,
              firstName: item.FIRSTNAME,
              lastName: item.LASTNAME,
              paid: Boolean(item.PAID),
              price: guestEntrant !== undefined ? guestEntrant.COST : 0,
              formType: FormType.GUEST,
            })
          });
        }
        if (formType === 'EVENTS') {
          paymentSummary[FormType.EVENT] = (data[formType as keyof PaymentSummaryResponse] as PaymentSummaryResponse['EVENT']).map((item: PaymentSummaryResponse['EVENT'][number]) => {
            const entrant: AllRegisteredEntrantsResponse[number] = this.getEntrantById(item.ENTRANT_ID);
            return {
              eventName: appDataStore.getEventDetailsById(item.ID).NAME,
              entrantName: `${entrant.FIRST_NAME} ${entrant.LAST_NAME}`,
              entrantId: item.ENTRANT_ID,
              eventId: item.ID,
              paid: Boolean(item.PAID),
              price: item.COST,
              carId: item.CAR_ID,
              formType: FormType.EVENT,
              seatsAvailable: item.SEATS_AVAILABLE,
            };
          });
        }
        if (formType === 'MEALS') {
          paymentSummary[FormType.MEAL] = (data[formType as keyof PaymentSummaryResponse] as PaymentSummaryResponse['MEAL']).map((item: PaymentSummaryResponse['MEAL'][number]) => {
            const entrant: AllRegisteredEntrantsResponse[number] = this.getEntrantById(item.ENTRANT_ID);
            return {
              mealName: appDataStore.getMealDetailsById(item.ID).NAME,
              entrantName: `${entrant.FIRST_NAME} ${entrant.LAST_NAME}`,
              entrantId: item.ENTRANT_ID,
              mealId: item.ID,
              paid: Boolean(item.PAID),
              price: item.COST,
              formType: FormType.MEAL,
            };
          });
        }
        if (formType === 'CARS') {
          paymentSummary[FormType.CAR] = (data[formType as keyof PaymentSummaryResponse] as PaymentSummaryResponse['CAR'])
            .map((item: PaymentSummaryResponse['CAR'][number]) => {
              return {
                year: item.YEAR,
                modelName: item.MODEL_NAME,
                color: item.COLOR,
                carId: item.CAR_ID,
                price: item.COST,
                paid: Boolean(item.PAID),
                isPorsche: Boolean(item.ISPORSCHE),
                legal: Boolean(item.LEGAL),
                MEMBERCAR_ID: item.MEMBERCAR_ID,
                formType: FormType.CAR,
              };
            })
            .sort((a: PaymentSummaryCarItem, b: PaymentSummaryCarItem) => {
              // If a is not a Porsche and b is a Porsche, a comes first (return -1)
              if (a.isPorsche === false && b.isPorsche === true) return -1;
              // If a is a Porsche and b is not a Porsche, b comes first (return 1)
              if (a.isPorsche === true && b.isPorsche === false) return 1;
              // Otherwise, maintain original order (return 0)
              return 0;
            });
        }
      }
      this.paymentSummary = paymentSummary;
    },

    setCompletionProgress(completionProgressResponse: CompletionProgressResponse): void {
      const completionProgress: CompletionProgress = {
        [CompletionProgressFormType.CHECKOUT]: false,
        [CompletionProgressFormType.COENTRANT]: false,
        [CompletionProgressFormType.GUEST]: false,
        [CompletionProgressFormType.PRIMARYENTRANT]: false,
        [CompletionProgressFormType.CHILD]: false,
      };
      for (const formType in completionProgressResponse) {
        if (formType === 'PRIMARYENTRANT') {
          const isComplete: boolean | undefined = completionProgressResponse[CompletionProgressFormType.PRIMARYENTRANT] === 0 ? false : true;
          if (isComplete === undefined) {
            throw new Error('setCompletionProgress: Missing PRIMARYENTRANT value from CompletionProgressResponse');
          }
          completionProgress[CompletionProgressFormType.PRIMARYENTRANT] = isComplete;
        }
        if (formType === 'COENTRANT') {
          const isComplete: boolean | undefined = completionProgressResponse[CompletionProgressFormType.COENTRANT] === 0 ? false : true;
          if (isComplete === undefined) {
            throw new Error('setCompletionProgress: Missing COENTRANT value from CompletionProgressResponse');
          }
          completionProgress[CompletionProgressFormType.COENTRANT] = isComplete;
        }
        if (formType === 'PCA_JUNIOR_OR_CHILD') {
          const isComplete: boolean | undefined = completionProgressResponse['PCA_JUNIOR_OR_CHILD'] === 0 ? false : true;
          if (isComplete === undefined) {
            throw new Error('setCompletionProgress: Missing PCA_JUNIOR_OR_CHILD value from CompletionProgressResponse');
          }
          completionProgress[CompletionProgressFormType.CHILD] = isComplete;
        }
        if (formType === CompletionProgressFormType.GUEST) {
          const isComplete: boolean | undefined = completionProgressResponse[CompletionProgressFormType.GUEST] === 0 ? false : true;
          if (isComplete === undefined) {
            throw new Error('setCompletionProgress: Missing GUEST value from CompletionProgressResponse');
          }
          completionProgress[CompletionProgressFormType.GUEST] = isComplete;
        }
        if (formType === CompletionProgressFormType.CHECKOUT) {
          const isComplete: boolean | undefined = completionProgressResponse[CompletionProgressFormType.CHECKOUT] === 0 ? false : true;
          if (isComplete === undefined) {
            throw new Error('setCompletionProgress: Missing CHECKOUT value from CompletionProgressResponse');
          }
          completionProgress[CompletionProgressFormType.CHECKOUT] = isComplete;
        }
      }
      this.completionProgress = completionProgress;
    },

    setAllRegisteredEntrants(data: AllRegisteredEntrantsResponse): void {
      this.allRegisteredEntrants = data;
    },

    async requestPrimaryEntrantDetails(): Promise<void> {
      const primaryEntrantDetailsResponse: PrimaryEntrantDetailsResponse | null = await fetchPrimaryEntrantDetails();
      this.setPrimaryEntrantDetailsData(primaryEntrantDetailsResponse);
    },

    async requestPaymentSummary(): Promise<void> {
      const paymentSummaryResponse: PaymentSummaryResponse = await fetchPaymentSummary();
      this.setPaymentSummaryData(paymentSummaryResponse);
    },

    async requestCompletionProgress(): Promise<void> {
      const completionProgressResponse: CompletionProgressResponse | null = await fetchCompletionProgress();
      if (completionProgressResponse !== null) {
        this.setCompletionProgress(completionProgressResponse);
      }
    },

    async requestAllRegisteredEntrantsAndPaymentSummary(): Promise<void> {
      // Order of operations is important here - the payment summary relies on the allRegisteredEntrants data to be up to date
      const allRegisteredEntrantsResponse: AllRegisteredEntrantsResponse = await fetchAllRegisteredEntrants();
      this.setAllRegisteredEntrants(allRegisteredEntrantsResponse);
      await this.requestPaymentSummary();
    },

    /**
     * @name getEventEntrantOptionsByEventId
     * @description Returns an array of registered entrants that are eligible to register for the passed event
     */
    getEventEntrantOptionsByEventId(eventId: number, entrantId: number, isEditMode: boolean): EventEntrantOption[] {
      const appDataStore = useAppDataStore();
      const entrants: EventEntrantOption[] = [];
      const event: ParadeEvent = appDataStore.getEventDetailsById(eventId);

      if (event.ENTER_BY === 'car') {
        // If the event is 'enter by car' we just pass the primary entrant
        const enterByCarPricingItem: PricingItem | undefined = event.PRICING.find((pricingItem: PricingItem) => pricingItem.EVENT_ENTRANT_TYPE_ID === 9);
        const primaryEntrant: PrimaryEntrantDetails | null = this.getPrimaryEntrantDetails;

        // Don't add the Primary entrant if the user doesn't have cars available
        const userHasCarsAvailable: boolean = this.getCarOptions(event.EVENT_ID, isEditMode).length > 0;
        if (userHasCarsAvailable) {
          if (enterByCarPricingItem === undefined) {
            throw new Error('getEventEntrantOptionsByEventId: enterByCarPricingItem is undefined');
          }
          if (primaryEntrant === null) {
            throw new Error('getEventEntrantOptionsByEventId: primary entrant details are null');
          }
          entrants.push({
            name: `${primaryEntrant.FIRST_NAME} ${primaryEntrant.LAST_NAME}`,
            id: primaryEntrant.ID,
            cost: enterByCarPricingItem.FEE,
            entrantType: 9, // Nine is car registration entrant type id
          });
        }
      } else if (entrantId !== 0) {
        // If entrantId is non-zero (meaning they're already registered & we're in EDIT mode), we only need to pass the requested entrant's details
        const registeredEntrant: AllRegisteredEntrantsResponse[number] = this.getEntrantById(entrantId);
        const name: string = `${registeredEntrant.FIRST_NAME} ${registeredEntrant.LAST_NAME}`;
        const pricingItem: PricingItem | undefined = event.PRICING.find((pricingItem: PricingItem) => pricingItem.EVENT_ENTRANT_TYPE_ID === registeredEntrant.ENTRANT_TYPE_ID);
        if (pricingItem === undefined) {
          throw new Error('getEventEntrantOptionsByEventId: Pricing item not found');
        }
        const entrantItem: EventEntrantOption = {
          name,
          id: entrantId,
          cost: pricingItem.FEE,
          entrantType: registeredEntrant.ENTRANT_TYPE_ID,
        };
        // For person&car events, only include the entrant in edit mode if they have a valid car
        if (event.ENTER_BY === 'person&car') {
          const userHasCarsAvailable: boolean = this.getCarOptions(event.EVENT_ID, isEditMode).length > 0;
          if (userHasCarsAvailable) {
            entrants.push(entrantItem);
          }
        } else {
          entrants.push(entrantItem);
        }
      } else {
        for (const entrant of [...this.allRegisteredEntrants].reverse()) {
          for (const pricingItem of event.PRICING) {
            if (entrant.ENTRANT_TYPE_ID === pricingItem.EVENT_ENTRANT_TYPE_ID) {
              // Check if the entrant is already registered for the event
              const registeredItem: PaymentSummaryEventItem | undefined = this.getPaymentSummary[FormType.EVENT].find(
                (event: PaymentSummaryEventItem) => event.eventId === eventId && event.entrantId === entrant.ID
              );
              if (registeredItem === undefined) {
                const entrantItem: EventEntrantOption = {
                  name: `${entrant.FIRST_NAME} ${entrant.LAST_NAME}`,
                  id: entrant.ID,
                  cost: pricingItem.FEE,
                  entrantType: entrant.ENTRANT_TYPE_ID,
                }
                if (event.ENTER_BY === 'person&car') {
                  // If it's a person&car event, we need to check the entrant has a valid car 
                  // and if so we only add the entrant item (the car selection is added in the component later)
                  const userHasCarsAvailable: boolean = this.getCarOptions(event.EVENT_ID, isEditMode).length > 0;
                  if (userHasCarsAvailable) {
                    entrants.push(entrantItem);
                  }
                } else {
                  entrants.push(entrantItem);
                }
              }
            }
          }
        }
      }
      return entrants;
    },

    /**
     * @name getEntrantById
     * @description Returns the entrant by id
     */
    getEntrantById(id: number): AllRegisteredEntrantsResponse[number] {
      const entrant: AllRegisteredEntrantsResponse[number] | undefined = this.getAllRegisteredEntrants.find((entrant: AllRegisteredEntrantsResponse[number]) => entrant.ID === id);
      if (entrant === undefined) {
        throw new Error('getEntrantsNameById: entrant is undefined');
      }
      return entrant;
    },

    getHasUnpaidItems(): boolean {
      return Object.values(this.getPaymentSummary)
        .flatMap((item: PaymentSummaryItem[]) => item)
        .some((item: PaymentSummaryItem) => !item.paid);
    },

    /**
     * @name getMealEntrantOptionsByMealId
     * @description Returns an array of registered entrants
     */
    getMealEntrantOptionsByMealId(mealId: number, entrantId: number): Omit<MealEntrantItem, 'MEAL_ID' | 'COST'>[] {
      const appDataStore = useAppDataStore();
      const entrants: Omit<MealEntrantItem, 'MEAL_ID' | 'COST'>[] = [];
      const meal: ParadeMeal = appDataStore.getMealDetailsById(mealId);
      const blankMealEntrant: Omit<MealEntrantItem, 'MEAL_ID' | 'COST'> = {
        NAME: '',
        ENTRANT_ID: 0,
        ENTRANT_TYPE_ID: 0,
        ENTREE_OPTION: 0,
        ID: 0,
      };

      if (entrantId !== 0) {
        const registeredEntrant: AllRegisteredEntrantsResponse[number] = this.getEntrantById(entrantId);
        const name: string = `${registeredEntrant.FIRST_NAME} ${registeredEntrant.LAST_NAME}`;
        entrants.push({
          // Setting some props for good measure, but it shouldn't be necessary since the setEditForm method sets everything we need
          ...blankMealEntrant,
          NAME: name,
          ENTRANT_ID: entrantId,
          ENTRANT_TYPE_ID: registeredEntrant.ENTRANT_TYPE_ID,
        });
      } else {
        // If entrantId is 0, we're in new mode - initialize the data for all eligible entrants
        const allowAdultEntrants: boolean = meal.ENTREES.some((entree: MealEntree) => entree.ADULT_MEAL === 1);
        const allowGuests: boolean = meal.ALLOW_GUESTS === 1;
        const allowChildEntrants: boolean = meal.ALLOW_CHILDREN === 1;

        // Add each entrant object based on meal entrant eligibility
        for (const entrant of this.allRegisteredEntrants) {
          const registeredItem: PaymentSummaryMealItem | undefined = this.getPaymentSummary[FormType.MEAL].find(
            (meal: PaymentSummaryMealItem) => meal.mealId === mealId && meal.entrantId === entrant.ID
          );

          if (registeredItem === undefined) {
            if (
              (allowAdultEntrants && (entrant.ENTRANT_TYPE_ID === 1 || entrant.ENTRANT_TYPE_ID === 2 || entrant.ENTRANT_TYPE_ID === 5)) ||
              entrant.ENTRANT_TYPE_ID === 6 ||
              entrant.ENTRANT_TYPE_ID === 7
            ) {
              entrants.push({
                ...blankMealEntrant,
                NAME: `${entrant.FIRST_NAME} ${entrant.LAST_NAME}`,
                ENTRANT_ID: entrant.ID,
                ENTRANT_TYPE_ID: entrant.ENTRANT_TYPE_ID,
              });
            }
            if (allowGuests && entrant.ENTRANT_TYPE_ID === 3) {
              entrants.push({
                ...blankMealEntrant,
                NAME: `${entrant.FIRST_NAME} ${entrant.LAST_NAME}`,
                ENTRANT_ID: entrant.ID,
                ENTRANT_TYPE_ID: entrant.ENTRANT_TYPE_ID,
              });
            }
            if (allowChildEntrants && entrant.ENTRANT_TYPE_ID === 8) {
              entrants.push({
                ...blankMealEntrant,
                NAME: `${entrant.FIRST_NAME} ${entrant.LAST_NAME}`,
                ENTRANT_ID: entrant.ID,
                ENTRANT_TYPE_ID: entrant.ENTRANT_TYPE_ID,
              });
            }
          }
        }
      }
      return entrants;
    },
  },
});
